import { cn } from "@arcadeai/design-system/lib/utils";
import { AlertTriangle, Check } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useSession } from "@/lib/auth-client";

type InvitePermissionWarningProps = {
  className?: string;
};

export function InvitePermissionWarning({
  className,
}: InvitePermissionWarningProps) {
  const posthog = usePostHog();
  const { data: session } = useSession();
  const [hasExpressedInterest, setHasExpressedInterest] = useState(false);

  const handleNotifyClick = () => {
    const email =
      typeof window === "undefined"
        ? undefined
        : (session?.user?.email ??
          localStorage.getItem("user_email") ??
          undefined);

    if (email) {
      posthog?.capture("rbac_interest_expressed", { email });
    }

    setHasExpressedInterest(true);
  };

  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2.5 transition-colors",
        hasExpressedInterest
          ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/50 dark:bg-emerald-950/20"
          : "border-amber-200/70 bg-amber-50/30 dark:border-amber-800/40 dark:bg-amber-950/20",
        className
      )}
    >
      {hasExpressedInterest ? (
        <div className="flex items-center gap-2">
          <Check className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <p className="text-emerald-700 text-xs dark:text-emerald-300">
            Thanks! We'll notify you when role-based permissions are available.
          </p>
        </div>
      ) : (
        <div className="flex gap-2.5">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-500 dark:text-amber-400" />
          <p className="text-amber-900/80 text-xs leading-relaxed dark:text-amber-200/90">
            People you invite will have full access to this project. Want more
            control?{" "}
            <button
              className="font-medium text-amber-700 underline underline-offset-2 transition-colors hover:text-amber-600 dark:text-amber-300 dark:hover:text-amber-200"
              onClick={handleNotifyClick}
              type="button"
            >
              Sign up to be notified
            </button>{" "}
            when advanced roles are in preview.
          </p>
        </div>
      )}
    </div>
  );
}
